import type {
  CandidateMatch,
  ClassifiedReqList,
  CvMatchResult,
  LockedCandidate,
  RequirementEvidence,
  RoleCandidatePool,
} from "../types"

const STATUS_LABEL: Record<RequirementEvidence["status"], string> = {
  matched: "Uppfyllt",
  missing: "Saknas",
  overqualified: "Övermeriterad",
}

function renderClassifiedRequirements(classified: ClassifiedReqList): string {
  const lines: string[] = []
  if (classified.hard.length > 0) {
    lines.push("**Hårda krav (objektiva/verifierbara):**")
    for (const r of classified.hard) {
      const overqNote = r.overqualifiedCounts ? " *(nivåkrav — överkvalificering räknas)*" : ""
      lines.push(`- ${r.text}${overqNote}`)
    }
  }
  if (classified.soft.length > 0) {
    lines.push("**Mjuka krav (beteendemässiga/subjektiva):**")
    for (const r of classified.soft) {
      lines.push(`- ${r.text}`)
    }
  }
  return lines.join("\n")
}

function renderCandidateEvidence(evidence: RequirementEvidence[]): string {
  const lines: string[] = []
  for (const ev of evidence) {
    const statusLabel = STATUS_LABEL[ev.status]
    lines.push(`**${ev.requirementText}** — ${statusLabel}`)
    if (ev.reasoning) lines.push(`  *Resonemang:* ${ev.reasoning}`)
    if (ev.sourceQuote) lines.push(`  *Källa (${ev.sourceDocument}):* "${ev.sourceQuote}"`)
  }
  return lines.join("\n")
}

function renderCandidateFallback(c: CandidateMatch): string {
  const parts: string[] = []
  if (c.matchedRequirements.length > 0)
    parts.push(`**Uppfyllda:** ${c.matchedRequirements.join(" · ")}`)
  if (c.missingRequirements.length > 0)
    parts.push(`**Saknade:** ${c.missingRequirements.join(" · ")}`)
  if (c.overqualifiedRequirements.length > 0)
    parts.push(`**Övermeriterad på:** ${c.overqualifiedRequirements.join(" · ")}`)
  if (c.meritingMatches.length > 0) parts.push(`**Meriterande:** ${c.meritingMatches.join(" · ")}`)
  return parts.join("\n")
}

function renderRolePool(pool: RoleCandidatePool, lockedCandidates: LockedCandidate[]): string {
  const lines: string[] = []
  const lockedIds = new Set(
    lockedCandidates.filter((lc) => lc.roleId === pool.roleId).map((lc) => lc.candidateId)
  )
  const filledCount = lockedIds.size

  let coverageLabel = "Ej bemannad"
  if (filledCount >= pool.quantityRequired) coverageLabel = "Fullbemannad"
  else if (filledCount > 0) coverageLabel = `${filledCount}/${pool.quantityRequired} bemannad`

  lines.push(`### ${pool.roleName} (${pool.quantityRequired} st) — ${coverageLabel}\n`)

  if (pool.classifiedRequirements) {
    lines.push(renderClassifiedRequirements(pool.classifiedRequirements))
    lines.push("")
  }

  if (pool.candidates.length === 0) {
    lines.push("*Inga kandidater matchade.*\n")
    return lines.join("\n")
  }

  lines.push("| Kandidat | Låst | Uppfyllda | Saknade | Övermerit. | Meriterande | Källa |")
  lines.push("|---|---|---|---|---|---|---|")
  for (const c of pool.candidates) {
    const locked = lockedIds.has(c.candidateId) ? "Ja" : "–"
    const matched = c.matchedRequirements.length
    const missing = c.missingRequirements.length
    const overq = c.overqualifiedRequirements.length
    const merit = c.meritingMatches.length
    const source = c.cvSource ?? "–"
    lines.push(
      `| ${c.displayName} | ${locked} | ${matched} | ${missing} | ${overq} | ${merit} | ${source} |`
    )
  }
  lines.push("")

  for (const c of pool.candidates) {
    lines.push(`#### ${c.displayName}`)
    if (c.requirementEvidence && c.requirementEvidence.length > 0) {
      lines.push(renderCandidateEvidence(c.requirementEvidence))
    } else {
      const fallback = renderCandidateFallback(c)
      if (fallback) lines.push(fallback)
    }
    if (c.meritingMatches.length > 0 && c.requirementEvidence && c.requirementEvidence.length > 0) {
      lines.push(`**Meriterande:** ${c.meritingMatches.join(" · ")}`)
    }
    if (c.notes) lines.push(`**Anteckningar:** ${c.notes}`)
    lines.push("")
  }

  return lines.join("\n")
}

function renderStaffingPlan(
  lockedCandidates: LockedCandidate[],
  pools: RoleCandidatePool[]
): string {
  if (lockedCandidates.length === 0) return ""
  const lines: string[] = ["\n### Bemanningsplan (låsta kandidater)\n"]

  const byRole = new Map<string, LockedCandidate[]>()
  for (const lc of lockedCandidates) {
    const arr = byRole.get(lc.roleId) ?? []
    arr.push(lc)
    byRole.set(lc.roleId, arr)
  }

  for (const [roleId, candidates] of byRole) {
    const pool = pools.find((p) => p.roleId === roleId)
    const roleName = pool?.roleName ?? roleId
    lines.push(`**${roleName}:** ${candidates.map((c) => c.displayName).join(", ")}`)
  }

  return lines.join("\n")
}

export function renderCvMatch(data: CvMatchResult): string {
  const lines: string[] = []

  for (const pool of data.rolePools) {
    lines.push(renderRolePool(pool, data.lockedCandidates))
  }

  lines.push(renderStaffingPlan(data.lockedCandidates, data.rolePools))

  return lines.join("\n")
}
